import React from 'react';
import PropTypes from 'prop-types';
import truncate from 'lodash/truncate';
import get from 'lodash/get';

import env from 'Env';
import { currencyCode } from 'Helpers/Geo/data';
import ErrorBoundary from 'Components/Utility/ErrorBoundary';
import generateAggregateReviews from 'Components/Utility/SEO/aggregate-review-structured';
import generateReviews from 'Components/Utility/SEO/review-structured';
import ProductRequirements from 'Helpers/Product/ProductRequirements';
import isPrerender from 'Helpers/SEO/is-prerender';
import { ScriptTag } from 'Helpers/SEO/Tags';

function ProductStructuredData({ product, reviews }) {
  if (!product) return false;

  const striptags = (content) => {
    const frag = document.createDocumentFragment();
    const innerEl = document.createElement('div');
    frag.appendChild(innerEl);
    innerEl.innerHTML = content;
    return truncate(frag.firstChild.innerText, { length: 250, separator: ' ' });
  };

  const schemaUrl = product.url;
  const schemaDescription = striptags(get(product, 'seo.desc') || product.desc);
  const schemaPublishers = get(product, 'publishers', []).map(p => p.name || p) || [];

  const url = `https://${env.appUrl}/${window.locale || 'en'}/${product.type || 'game'}/${product.slug}`;

  let availability = 'InStock';

  if (product.sold_out) {
    availability = 'SoldOut';
  }

  if (ProductRequirements.isPreorder(product)) {
    availability = 'PreOrder';
  }

  const schema = JSON.stringify({
    '@context': 'https://schema.org',
    '@id': `${url}#product`,
    '@type': 'Product',
    name: `${product.name}`,
    url,
    mainEntityOfPage: {
      '@id': `${url}#webpage`,
      '@type': 'ItemPage',
    },
    image: [
      `${env.cdnExtUrl}/product/1280x720/${product.cover}`,
      ...product.img.map(i => `${env.cdnExtUrl}/product/1280x720/${i.slug}`),
    ],
    description: schemaDescription,
    brand: schemaPublishers ? {
      '@type': 'Brand',
      name: truncate(schemaPublishers.join(', '), { length: 70, separator: ',' }),
      url: schemaUrl,
    } : {
      '@type': 'Brand',
      name: 'Fanatical',
      url: `https://${env.appUrl}/${window.locale || 'en'}/`,
    },
    releaseDate: product.release_date || undefined,
    sku: product._id,
    // If we want to turn back on multiple currency offers
    // offers: Object.keys(product.price).map((code) => {
    offers: [isPrerender() ? 'USD' : currencyCode].map((code) => {
      const offer = {
        '@type': 'Offer',
        price: get(product, `currentPrice.${code}`, get(product, `price.${code}`, 0)) / 100,
        priceCurrency: code,
        availability,
        priceValidUntil: new Date(Date.now() + (1000 * 60 * 60 * 48)).toISOString(), // default
        url: `https://${env.appUrl}/${window.locale || 'en'}/${product.type || 'game'}/${product.slug}`,
      };

      if (product.current_discount &&
        product.current_discount.percent &&
        product.current_discount.until
      ) {
        offer.priceValidUntil = product.current_discount.until;
      }

      if (product.bundles && product.bundles.length > 1) {
        offer['@type'] = 'AggregateOffer';
        offer.lowPrice = offer.price;
        const topTier = product.bundles[product.bundles.length - 1];
        offer.highPrice = get(topTier, `currentPrice.${code}`, get(topTier, `price.${code}`, 0)) / 100;
        offer.offerCount = product.bundles.length;
        offer.price = undefined;
      }

      return offer;
    }),
    aggregateRating: generateAggregateReviews(get(product, 'userReviewSummary'), product, true),
    review: generateReviews(reviews, product, true),
  });

  if (product.hitcardVideo) {
    const videoUrl = `${env.videoCdnUrl}${product.hitcardVideo}`;

    const videoSchema = JSON.stringify({
      '@context': 'https://schema.org',
      '@id': `${url}#video`,
      '@type': 'VideoObject',
      isPartOf: {
        '@id': `${url}#webpage`,
        '@type': 'ItemPage',
      },
      name: `${product.name} Preview Video`,
      uploadDate: product.release || product.release_date,
      description: schemaDescription,
      thumbnailUrl: [videoUrl.replace('-480p.mp4', '-poster.jpg')],
      contentUrl: videoUrl,
    });

    return (
      <>
        <ScriptTag id="product-schema" type="application/ld+json" script={schema} />
        <ScriptTag id="product-video-schema" type="application/ld+json" script={videoSchema} />
      </>
    );
  }

  return <ScriptTag id="product-schema" type="application/ld+json" script={schema} />;
}

export default function ProductStructuredDataWrapper(props) {
  return (
    <ErrorBoundary hideForm>
      <ProductStructuredData {...props} />
    </ErrorBoundary>
  );
}

ProductStructuredData.propTypes = {
  product: PropTypes.shape({}).isRequired,
  reviews: PropTypes.arrayOf(PropTypes.shape({})),
};

ProductStructuredData.defaultProps = {
  reviews: [],
};
